import React, { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { RefreshCcw, Award, Timer } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LoyaltyStampQrPanel
 *
 * Admin/staff-facing screen that mints a 5-minute, single-use loyalty QR.
 * The diner opens Mamamina's mobile app → Account → "Scan a stamp QR",
 * points the camera here, and picks up a stamp. Nice-to-print sized so it
 * can live on a stand next to the register.
 */
export default function LoyaltyStampQrPanel() {
  const [qr, setQr] = useState(null);
  const [expiresIn, setExpiresIn] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mint = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/loyalty/stamp-qr`,
        {},
        { withCredentials: true, headers: withCsrf() }
      );
      setQr(data);
      setExpiresIn(data.ttl_seconds || 300);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't create a QR. Are you signed in as staff/admin?";
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  };

  // Read the CSRF cookie for state-changing calls (backend enforces it when
  // the httpOnly session cookie is present).
  function withCsrf() {
    if (typeof document === "undefined") return {};
    const m = document.cookie.match(/(?:^|; )mm_csrf=([^;]+)/);
    return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
  }

  useEffect(() => {
    mint();
  }, []);

  // Countdown timer with auto-refresh when it hits zero.
  useEffect(() => {
    if (expiresIn <= 0) return;
    const id = setInterval(() => {
      setExpiresIn((s) => {
        if (s <= 1) {
          mint();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [expiresIn]);

  const mins = Math.floor(expiresIn / 60);
  const secs = expiresIn % 60;

  return (
    <div className="max-w-3xl mx-auto py-8 px-6" data-testid="loyalty-qr-panel">
      <div className="flex items-center gap-3 mb-2">
        <Award size={22} className="text-[var(--mm-accent)]" />
        <h2 className="text-2xl font-serif italic text-[var(--mm-cream)]">Loyalty stamp QR</h2>
      </div>
      <p className="text-sm text-[var(--mm-cream)]/70 mb-8 max-w-xl">
        Show this QR to a diner after they pay. They open the Mamamina mobile app,
        tap <span className="text-[var(--mm-accent)]">Account → Scan a stamp QR</span>, and pick up their stamp.
        Every code is single-use and refreshes every 5 minutes for security.
      </p>

      {error && (
        <div className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-300 mb-6">
          {error}
        </div>
      )}

      <div className="bg-[var(--mm-cream)] p-10 flex flex-col items-center gap-6 border border-[var(--mm-accent)]/40">
        {qr ? (
          <QRCodeSVG
            value={qr.qr_payload}
            size={320}
            level="M"
            fgColor="#14100c"
            bgColor="#efe5d3"
            data-testid="loyalty-qr-svg"
          />
        ) : (
          <div className="w-[320px] h-[320px] flex items-center justify-center text-[var(--mm-charcoal)]/50">
            {loading ? "Preparing…" : "No QR yet"}
          </div>
        )}
        {qr && (
          <div className="flex items-center gap-2 text-[var(--mm-charcoal)] text-sm">
            <Timer size={14} />
            <span className="tracking-[0.24em] uppercase text-[11px]">
              Refreshes in {String(mins).padStart(1, "0")}:{String(secs).padStart(2, "0")}
            </span>
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={mint}
          disabled={loading}
          data-testid="loyalty-qr-refresh"
          className="border border-[var(--mm-accent)] px-5 py-2.5 text-xs tracking-[0.24em] uppercase text-[var(--mm-accent)] hover:bg-[var(--mm-accent)] hover:text-[var(--mm-black)] transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          <RefreshCcw size={12} /> Mint a fresh QR
        </button>
      </div>
    </div>
  );
}
